import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Icons from 'components/Icons';
import { useGame, useDictionary } from 'common/state';
import { getAttemptsData, removeAccents } from 'common/logic';

const getKeyboardData = (attemptsData) => {
  const keyboardData = {};
  for (const word of attemptsData) {
    for (const letterData of word) {
      const { letter: letterWithAccent, classes } = letterData;
      const letter = removeAccents(letterWithAccent);
      if (classes.includes('absent') && !keyboardData[letter]) {
        keyboardData[letter] = 'wrong';
      } else if (
        classes.includes('present') &&
        (!keyboardData[letter] ||
          (keyboardData[letter] && keyboardData[letter] !== 'right'))
      ) {
        keyboardData[letter] = 'place';
      } else if (
        classes.includes('head') ||
        classes.includes('tail') ||
        classes.includes('one-present')
      ) {
        keyboardData[letter] = 'right';
      }
    }
  }
  return keyboardData;
};

function Keyboard() {
  const { language } = useParams();
  const [gameState, gameActions] = useGame();
  const [dictionary] = useDictionary();
  const [keyboardData, setKeyboardData] = useState({});

  useEffect(() => {
    if (!gameState.spec) {
      return;
    }
    const { answer } = gameState.spec;
    const { attempts } = gameState.data;

    const attemptsData = getAttemptsData(answer, attempts);
    setKeyboardData(getKeyboardData(attemptsData));
  }, [gameState]);

  const handleButtonClick = useCallback(
    (key) => {
      gameActions.input(key, dictionary);
    },
    [dictionary, gameActions],
  );

  return (
    <div className="keyboard-wrapper">
      <div className={`keyboard ${language === 'no' ? 'no' : ''}`}>
        <button
          id="key_q"
          onClick={() => handleButtonClick('q')}
          className={keyboardData['q']}
        >
          q
        </button>
        <button
          id="key_w"
          onClick={() => handleButtonClick('w')}
          className={keyboardData['w']}
        >
          w
        </button>
        <button
          id="key_e"
          onClick={() => handleButtonClick('e')}
          className={keyboardData['e']}
        >
          e
        </button>
        <button
          id="key_r"
          onClick={() => handleButtonClick('r')}
          className={keyboardData['r']}
        >
          r
        </button>
        <button
          id="key_t"
          onClick={() => handleButtonClick('t')}
          className={keyboardData['t']}
        >
          t
        </button>
        <button
          id="key_y"
          onClick={() => handleButtonClick('y')}
          className={keyboardData['y']}
        >
          y
        </button>
        <button
          id="key_u"
          onClick={() => handleButtonClick('u')}
          className={keyboardData['u']}
        >
          u
        </button>
        <button
          id="key_i"
          onClick={() => handleButtonClick('i')}
          className={keyboardData['i']}
        >
          i
        </button>
        <button
          id="key_o"
          onClick={() => handleButtonClick('o')}
          className={keyboardData['o']}
        >
          o
        </button>
        <button
          id="key_p"
          onClick={() => handleButtonClick('p')}
          className={keyboardData['p']}
        >
          p
        </button>
        {language === 'no' && (
          <button
            id="key_å"
            onClick={() => handleButtonClick('å')}
            className={keyboardData['å']}
          >
            å
          </button>
        )}
        <button
          id="key_a"
          onClick={() => handleButtonClick('a')}
          className={`${keyboardData['a']} ${language === 'no' ? 'no' : ''}`}
        >
          a
        </button>
        <button
          id="key_s"
          onClick={() => handleButtonClick('s')}
          className={keyboardData['s']}
        >
          s
        </button>
        <button
          id="key_d"
          onClick={() => handleButtonClick('d')}
          className={keyboardData['d']}
        >
          d
        </button>
        <button
          id="key_f"
          onClick={() => handleButtonClick('f')}
          className={keyboardData['f']}
        >
          f
        </button>
        <button
          id="key_g"
          onClick={() => handleButtonClick('g')}
          className={keyboardData['g']}
        >
          g
        </button>
        <button
          id="key_h"
          onClick={() => handleButtonClick('h')}
          className={keyboardData['h']}
        >
          h
        </button>
        <button
          id="key_j"
          onClick={() => handleButtonClick('j')}
          className={keyboardData['j']}
        >
          j
        </button>
        <button
          id="key_k"
          onClick={() => handleButtonClick('k')}
          className={keyboardData['k']}
        >
          k
        </button>
        <button
          id="key_l"
          onClick={() => handleButtonClick('l')}
          className={keyboardData['l']}
        >
          l
        </button>
        {language === 'no' && (
          <>
            <button
              id="key_ø"
              onClick={() => handleButtonClick('ø')}
              className={keyboardData['ø']}
            >
              ø
            </button>
            <button
              id="key_æ"
              onClick={() => handleButtonClick('æ')}
              className={keyboardData['æ']}
            >
              æ
            </button>
          </>
        )}
        {language !== 'no' && (
          <button
            id="key_backspace"
            onClick={() => handleButtonClick('Backspace')}
          >
            <Icons.Backspace />
          </button>
        )}
        {language === 'no' && (
          <button
            id="key_enter"
            onClick={() => handleButtonClick('Enter')}
            className={language === 'no' ? 'no' : ''}
          >
            Enter
          </button>
        )}
        <button
          id="key_z"
          onClick={() => handleButtonClick('z')}
          className={`${keyboardData['z']} ${language === 'no' ? 'no' : ''}`}
        >
          z
        </button>
        <button
          id="key_x"
          onClick={() => handleButtonClick('x')}
          className={keyboardData['x']}
        >
          x
        </button>
        <button
          id="key_c"
          onClick={() => handleButtonClick('c')}
          className={keyboardData['c']}
        >
          c
        </button>
        <button
          id="key_v"
          onClick={() => handleButtonClick('v')}
          className={keyboardData['v']}
        >
          v
        </button>
        <button
          id="key_b"
          onClick={() => handleButtonClick('b')}
          className={keyboardData['b']}
        >
          b
        </button>
        <button
          id="key_n"
          onClick={() => handleButtonClick('n')}
          className={keyboardData['n']}
        >
          n
        </button>
        <button
          id="key_m"
          onClick={() => handleButtonClick('m')}
          className={keyboardData['m']}
        >
          m
        </button>
        {language === 'no' && (
          <button
            id="key_backspace"
            onClick={() => handleButtonClick('Backspace')}
            className="no"
          >
            <Icons.Backspace />
          </button>
        )}
        {language !== 'no' && (
          <button
            id="key_enter"
            onClick={() => handleButtonClick('Enter')}
            className={language === 'no' ? 'no' : ''}
          >
            Enter
          </button>
        )}
      </div>
    </div>
  );
}

export default Keyboard;
